import {
  COMPANY_NAME_PLACEHOLDER,
  CONTACT_FIRST_NAME_PLACEHOLDER,
  DOCUMENT_LIST_PLACEHOLDER,
  ONBOARDING_DOCUMENT_REQUEST_SECTION,
  PORTAL_LINK_PLACEHOLDER,
} from './OnboardingEmailModal.templates';
import { OnboardingEmailTemplate, OnboardingTemplateDocument } from './OnboardingEmailModal.types';

export const normalizeDocumentName = (name: string): string =>
  name.trim().toLowerCase().replace(/\s+/g, ' ');

export const namesMatch = (left: string, right: string): boolean => {
  const a = normalizeDocumentName(left);
  const b = normalizeDocumentName(right);
  if (!a || !b) {
    return false;
  }
  return a === b || a.includes(b) || b.includes(a);
};

export const formatCombinedEmail = (subject: string, body: string): string => {
  const trimmedSubject = subject.trim();
  const trimmedBody = body.trim();
  if (trimmedSubject && trimmedBody) {
    return `Subject: ${trimmedSubject}\n\n${trimmedBody}`;
  }
  return trimmedSubject ? `Subject: ${trimmedSubject}` : trimmedBody;
};

// Inverse of formatCombinedEmail: split a stored draft into its editable
// Subject and body fields. Drafts without a subject line are treated as body.
export const parseCombinedEmail = (combined: string): { subject: string; body: string } => {
  const text = combined ?? '';
  const match = /^[ \t]*Subject:[ \t]*(.*)(?:\r?\n)?/.exec(text);
  if (!match) {
    return { subject: '', body: text.trim() };
  }
  return { subject: match[1].trim(), body: text.slice(match[0].length).trim() };
};

// Host + /documents/requests/<id>/<hmac> with or without a scheme.
const DOCUMENT_PORTAL_URL_PATTERN =
  /(?:https?:\/\/)?[^\s<>"']+\/documents\/requests\/\d+\/[a-fA-F0-9]+/g;

// Drafted email stores the portal URL as plain text. When a newer document
// request is created, that substring goes stale; swap it for the live link.
export const replaceDocumentPortalLink = (text: string, portalLink: string): string => {
  if (!text || !portalLink) {
    return text;
  }
  return text.replace(DOCUMENT_PORTAL_URL_PATTERN, portalLink);
};

export type OnboardingItemSection<T> = {
  group?: string;
  items: T[];
};

// Ungrouped items keep their position; grouped items collect under the first
// occurrence of their group so the checklist and email show each group once.
export const groupOnboardingItems = <T extends { group?: string }>(
  items: T[]
): OnboardingItemSection<T>[] => {
  const sections: OnboardingItemSection<T>[] = [];
  const sectionsByGroup = new Map<string, OnboardingItemSection<T>>();
  items.forEach(item => {
    const group = item.group?.trim();
    if (!group) {
      const lastSection = sections[sections.length - 1];
      if (lastSection && !lastSection.group) {
        lastSection.items.push(item);
      } else {
        sections.push({ items: [item] });
      }
      return;
    }
    const existingSection = sectionsByGroup.get(group);
    if (existingSection) {
      existingSection.items.push(item);
      return;
    }
    const section = { group, items: [item] };
    sectionsByGroup.set(group, section);
    sections.push(section);
  });
  return sections;
};

export const findOnboardingTemplateForValuationType = <T extends Pick<OnboardingEmailTemplate, 'valuationTypes'>>(
  templates: T[],
  valuationType?: string | null
): T | undefined => {
  const normalizedType = valuationType?.trim().toLowerCase();
  if (!normalizedType) {
    return undefined;
  }
  return templates.find(template =>
    (template.valuationTypes ?? []).some(type => type.trim().toLowerCase() === normalizedType)
  );
};

export const DEFAULT_REQUEST_VALUATION_TYPE = '409a';

export const getRequestValuationType = (
  template?: Pick<OnboardingEmailTemplate, 'requestValuationType'> | null
): string => template?.requestValuationType ?? DEFAULT_REQUEST_VALUATION_TYPE;

// Numbering runs across groups; each group gets a heading line.
export const formatDocumentList = (documents: OnboardingTemplateDocument[]): string => {
  let itemNumber = 0;
  return groupOnboardingItems(documents)
    .map(section => {
      const lines = section.items.map(document => {
        itemNumber += 1;
        const description = document.description?.trim();
        const line = description ? `${document.name} [${description}]` : document.name;
        return `${itemNumber}. ${line}`;
      });
      return (section.group ? [`${section.group}:`, ...lines] : lines).join('\n');
    })
    .join('\n\n');
};

export const composeOnboardingEmail = ({
  template,
  companyName,
  contactFirstName,
  portalLink,
  requestedDocuments,
}: {
  template: OnboardingEmailTemplate;
  companyName?: string | null;
  contactFirstName?: string | null;
  portalLink: string;
  requestedDocuments: OnboardingTemplateDocument[];
}): string => {
  const resolvedCompany = companyName?.trim() || 'there';
  const resolvedContact = contactFirstName?.trim() || 'there';
  const applyPlaceholders = (text: string) =>
    text
      .replaceAll(CONTACT_FIRST_NAME_PLACEHOLDER, resolvedContact)
      .replaceAll(COMPANY_NAME_PLACEHOLDER, resolvedCompany);

  const introduction = applyPlaceholders(template.introduction ?? '').trim();

  const standardSection = ONBOARDING_DOCUMENT_REQUEST_SECTION.replaceAll(
    DOCUMENT_LIST_PLACEHOLDER,
    formatDocumentList(requestedDocuments)
  ).replaceAll(PORTAL_LINK_PLACEHOLDER, portalLink);

  const body = [introduction, standardSection].filter(Boolean).join('\n\n');
  return formatCombinedEmail(applyPlaceholders(template.emailSubject), body);
};
